<script setup lang="ts">
import { type ChipResponse, InitializationStatus } from "mws-fabricks-ts-sdk";

import PageWrapper from "@/common/components/PageWrapper.vue";
import PaginatedTable from "@/common/components/table/PaginatedTable.vue";
import type { TableColumn } from "@/common/components/table/types";
import { startCase } from "@/common/utils/startCase";
import { useGetFabricksChipsListQuery } from "@/fabricks/chips/api/queries";
import ChipInitializationStatusBadge from "@/fabricks/chips/components/ChipInitializationStatusBadge.vue";
import TapAuthenticationSchemeBadge from "@/fabricks/chips/components/TapAuthenticationSchemeBadge.vue";

definePage({
  meta: {
    auth: ["FabricksAdmin.Access"],
  },
});

const chipInitializationStatusOptions = Object.values(InitializationStatus).map(
  (status) => ({
    label: startCase(status),
    value: status,
  }),
);

const columns: TableColumn<ChipResponse>[] = [
  {
    key: "uid",
    label: "ID",
    type: "link",
    pathConstructor: (chip) => `/fabricks/chips/${chip.id}/details`,
  },
  {
    key: "dateCreated",
    label: "Date Created",
    type: "date",
  },
  {
    key: "initializationStatus",
    label: "Status",
    type: "slot",
  },
  {
    key: "initializationSuccess",
    label: "Init Complete",
    type: "bool",
  },
  {
    key: "lastSchemeUsed",
    label: "Tap Scheme",
    type: "slot",
  },
  {
    key: "product.name",
    label: "Product",
    type: "link",
    pathConstructor: (chip) =>
      `/fabricks/trackable-products/details/${chip.product?.id}`,
  },
];

const breadcrumbLinks = [
  {
    label: "Fabricks Chips",
    to: "#",
  },
];
</script>

<template>
  <PageWrapper
    :breadcrumb-links="breadcrumbLinks"
    data-cy="fabricks-chips-list"
    title="Fabricks Chips"
  >
    <PaginatedTable
      :collection-query-fn="useGetFabricksChipsListQuery"
      :collection-options="{
        initialFilters: {
          chipInitializationStatuses: [],
        },
      }"
      :columns="columns"
      :is-searchable="true"
    >
      <template #filters="{ filters }">
        <FormKit
          v-model="filters.chipInitializationStatuses"
          :options="chipInitializationStatusOptions"
          placeholder="Status"
          close-icon="close"
          data-cy="chip-initialization-status-filter"
          data-test="chip-initialization-status-filter"
          inner-class="min-w-[14rem] md:max-w-none"
          name="chipInitializationStatusFilter"
          :open-on-click="true"
          outer-class="!w-auto"
          select-icon="down"
          selected-icon="check"
          tag-class="whitespace-nowrap"
          type="taglist"
        >
          <template #option="{ option }">
            <div
              :data-cy="`chip-intitialization-status-${option.label}`"
              class="formkit-option"
            >
              <span class="whitespace-nowrap">{{ option.label }}</span>
            </div>
          </template>
        </FormKit>
      </template>
      <template #initializationStatus="{ item }">
        <ChipInitializationStatusBadge :chip="item" />
      </template>
      <template #lastSchemeUsed="{ item }">
        <TapAuthenticationSchemeBadge :tap-scheme="item.lastSchemeUsed" />
      </template>
    </PaginatedTable>
  </PageWrapper>
</template>
